import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';
import { from, firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';

function authStateOnce(auth: Auth): Promise<any> {
  return new Promise(resolve => {
    const unsub = onAuthStateChanged(auth, user => {
      unsub();
      resolve(user);
    });
  });
}

/**
 * Block unauthenticated users - stores return URL for post-login redirect
 */
export const authGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const router = inject(Router);
  const auth = inject(Auth);
  const authService = inject(AuthService);

  const user = await authStateOnce(auth);

  if (user) return true;

  // Store the URL the user was trying to access
  authService.setReturnUrl(state.url);
  router.navigate(['/login']);
  return false;
};

/**
 * Allow only users with verified email
 */
export const verifiedEmailGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const router = inject(Router);
  const auth = inject(Auth);
  const authService = inject(AuthService);

  const user = await authStateOnce(auth);

  if (!user) {
    authService.setReturnUrl(state.url);
    router.navigate(['/login']);
    return false;
  }

  await user.reload();

  if (user.emailVerified) return true;

  router.navigate(['/verify-email']);
  return false;
};

/**
 * Prevent authenticated users from seeing login/register
 */
export const redirectIfAuthenticatedGuard: CanActivateFn = async () => {
  const router = inject(Router);
  const auth = inject(Auth);

  const user = await authStateOnce(auth);

  if (user) {
    router.navigate(['/models']);
    return false;
  }

  return true;
};

/**
 * Prevent already verified users from verify-email page
 */
export const redirectIfVerifiedGuard: CanActivateFn = async () => {
  const router = inject(Router);
  const auth = inject(Auth);

  const user = await authStateOnce(auth);

  if (!user) {
    router.navigate(['/login']);
    return false;
  }

  await user.reload();

  if (user.emailVerified) {
    router.navigate(['/models']);
    return false;
  }

  return true;
};
